/* eslint-disable rxjs/finnish */
import type { DatabaseBlockComponent } from '@blocksuite/blocks';
import type { ReadonlySignal } from '@lit-labs/preact-signals';
import { LiveData, Service } from '@toeverything/infra';
import { debounceTime } from 'rxjs';


interface SelectColumnOption {
  id: string;
  value: string;
}

interface SelectColumnDataWithOptions {
  options: SelectColumnOption[];
}

function hasSelectOptions(data: unknown): data is SelectColumnDataWithOptions {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  if (!('options' in d) || !Array.isArray(d['options'])) return false;
  return d['options'].every(
    opt =>
      typeof opt === 'object' &&
      opt !== null &&
      typeof (opt as Record<string, unknown>)['id'] === 'string' &&
      typeof (opt as Record<string, unknown>)['value'] === 'string'
  );
}

type BlockSingleView = {
  columns$: ReadonlySignal<string[]>;
  columnGetType: (id: string) => string | undefined;
  columnGetName: (id: string) => string;
  columnGetData: (id: string) => Record<string, unknown>;
};

type BlockDataSource = {
  rows$: ReadonlySignal<string[]>;
  viewManager: {
    currentViewId$: ReadonlySignal<string | undefined>;
    viewGet: (id: string) => BlockSingleView;
  };
  cellChangeValue: (rowId: string, propertyId: string, value: unknown) => void;
  rowDelete: (ids: string[]) => void;
};

function getDatabaseBlockId(
  databaseBlock: DatabaseBlockComponent
): string | undefined {
  const model = (databaseBlock as unknown as { model?: { id?: unknown } }).model;
  return typeof model?.id === 'string' ? model.id : undefined;
}

interface DatabaseColumnMapping {
  titleColId: string | undefined;
  statusColId: string | undefined;
  assigneeColId: string | undefined;
  progressColId: string | undefined;
  dueDateColId: string | undefined;
  priorityColId: string | undefined;
}

import { getActivePageIds } from '../../../hooks/use-active-pages';
import type { WorkspacePropertiesAdapter } from './adapter';
import { normalizeEmail } from './attendee-id-utils';
import {
  type ActionItemGroupBy,
  type ActionItemPriority,
  ActionItemPrioritySchema,
  type ActionItemQuery,
  ActionItemQuerySchema,
  ActionItemStatusSchema,
  type AggregationType,
  AggregationTypeSchema,
  type BreakdownStep,
  PRIORITY_ORDER,
  priorityWeight,
  type SerializedAttendee,
  type SerializedTodoItem,
} from './schema';
import { isActionItemCompleted, isActionItemDeleted } from './utils';

export const NO_DUE_DATE_KEY = 'no-due-date';
export const NO_DUE_DATE_LABEL = 'No Due Date';
export const OVERDUE_COLOR = 'var(--affine-error-color, #E5484D)';
export const DEFAULT_PLANNED_FOCUS_MINUTES = 60;

export function getPlannedFocusMinutes(
  item: Pick<SerializedTodoItem, 'plannedDuration'>,
  fallback: number = DEFAULT_PLANNED_FOCUS_MINUTES
): number {
  const planned = item.plannedDuration;
  if (typeof planned === 'number' && Number.isFinite(planned) && planned > 0) {
    return Math.round(planned);
  }
  return fallback;
}

export function getPlannedFocusSeconds(
  item: Pick<SerializedTodoItem, 'plannedDuration'>,
  fallback?: number
): number {
  const minutes = getPlannedFocusMinutes(item, fallback);
  return Math.max(1, Math.floor(minutes)) * 60;
}

export function isFocusScheduleOverdue(
  item: Pick<
    SerializedTodoItem,
    'scheduledAt' | 'plannedDuration' | 'status' | 'progress'
  >,
  nowMs: number = Date.now()
): boolean {
  if (!item.scheduledAt) return false;
  if (isActionItemCompleted(item as SerializedTodoItem)) return false;
  const start = new Date(item.scheduledAt);
  if (Number.isNaN(start.getTime())) return false;
  const plannedMinutes = getPlannedFocusMinutes(item);
  const endMs = start.getTime() + plannedMinutes * 60000;
  return endMs < nowMs;
}

function parseToLocalDate(
  date: Date | string | number | null | undefined
): Date | null {
  if (!date) return null;
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof date === 'number') {
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof date !== 'string') return null;
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (match) {
    const yearStr = match[1];
    const monthStr = match[2];
    const dayStr = match[3];
    if (yearStr && monthStr && dayStr) {
      const d = new Date(
        Number(yearStr),
        Number(monthStr) - 1,
        Number(dayStr)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatRelativeDayLabel(
  date: Date | string | number | null | undefined
): string {
  const d = parseToLocalDate(date);
  if (!d) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  if (isSameLocalDay(dd, today)) return 'Today';
  if (isSameLocalDay(dd, yesterday)) return 'Yesterday';
  if (isSameLocalDay(dd, tomorrow)) return 'Tomorrow';
  return '';
}

export function formatDateShort(
  date: Date | string | number | null | undefined
): string {
  const d = parseToLocalDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDateLong(
  date: Date | string | number | null | undefined
): string {
  const d = parseToLocalDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDueDateForDisplay(
  date: Date | string | null | undefined
): string {
  return formatDateShort(date);
}

export function formatDueDateForGroupTitle(
  date: Date | string | null | undefined
): string {
  const d = parseToLocalDate(date);
  if (!d) return NO_DUE_DATE_LABEL;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function parseDueDateToKey(
  date: Date | string | null | undefined
): string {
  const d = parseToLocalDate(date);
  if (!d) return NO_DUE_DATE_KEY;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function isDueDateOverdue(
  date: Date | string | null | undefined
): boolean {
  const d = parseToLocalDate(date);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

export interface ActionItemGroup {
  id: string;
  title: string;
  createDate: number;
  updateDate: number;
  items: SerializedTodoItem[];
  // Optional metadata for different group types
  metadata?: {
    groupBy?: ActionItemGroupBy;
    groupValue?: unknown;
    assigneeId?: string;
    assigneeName?: string;
    assigneeEmail?: string;
    assigneeAvatar?: string;
    totalItems?: number;
    pageId?: string;
    dueDate?: string;
    status?: string;
    isDocumentGroup?: boolean;
    dueDateGroup?: string;
    meetingDateLabel?: string;
    completedItems?: number;
    unassigned?: boolean;
  };
}

interface Disposable {
  dispose: () => void;
}

export class ActionItemsService extends Service {
  private readonly _actionItems$ = new LiveData<
    Record<string, SerializedTodoItem[]>
  >({});
  private readonly _actionItemsGrouped$ = new LiveData<ActionItemGroup[]>([]);
  private _docUpdateSubscription: Disposable | null = null;
  private _docMetaUpdateSubscription: Disposable | null = null;
  private _currentGroupingStrategy: ActionItemGroupBy = 'pageId';
  private _currentCompletionFilter: 'open' | 'closed' | 'all' = 'open';
  private _cacheDirty = true;
  private _cachedItems: SerializedTodoItem[] = [];
  private _cachedItemsByPage: Record<string, SerializedTodoItem[]> = {};
  private _cachedItemsById = new Map<string, SerializedTodoItem>();
  private _refreshScheduled = false;
  private _refreshPending = false;
  private _syncScheduled = false;
  private readonly _pendingSyncPages = new Set<string>();
  private _batchDepth = 0;
  private _isDisposed = false;

  // Database synchronization tracking
  private readonly _databaseBlocks = new Map<string, DatabaseBlockComponent>(); // pageId -> database block
  private readonly _syncInProgress = new Set<string>(); // prevent sync loops

  // Focus mode tracking (persisted as cumulative seconds per item)
  private readonly _focusedItemId$ = new LiveData<string | null>(null);
  private readonly _focusedSessionStartedAtMs$ = new LiveData<number | null>(
    null
  );
  private _focusStartedAtMs: number | null = null;
  private _focusTick: number | null = null;

  constructor(private readonly propertiesAdapter: WorkspacePropertiesAdapter) {
    super();
    this._markCacheDirty();
    this._requestRefresh();

    const subscription = this.propertiesAdapter.properties$
      .pipe(debounceTime(50))
      .subscribe(() => {
        this._markCacheDirty();
        this._requestRefresh();
      });

    this._docUpdateSubscription = {
      dispose: () => subscription.unsubscribe(),
    };

    const docMeta = this.propertiesAdapter.workspace?.docCollection?.meta;
    if (docMeta) {
      this._docMetaUpdateSubscription = docMeta.docMetaUpdated.on(() => {
        this._markCacheDirty();
        this._requestRefresh();
      });
    }
  }

  private _markCacheDirty(): void {
    this._cacheDirty = true;
  }

  private _requestRefresh(): void {
    if (this._isDisposed) return;
    if (this._refreshScheduled) {
      this._refreshPending = true;
      return;
    }
    this._refreshScheduled = true;
    queueMicrotask(() => {
      this._executeRefresh();
    });
  }

  private _executeRefresh(): void {
    if (this._isDisposed) return;
    this._refreshScheduled = false;
    const hadPending = this._refreshPending;
    this._refreshPending = false;

    if (this._batchDepth > 0) {
      this._refreshPending = true;
      return;
    }

    this._rebuildCacheIfNeeded();
    this._refreshObservables(false);

    if (hadPending) {
      this._requestRefresh();
    }
  }

  private _rebuildCacheIfNeeded(): void {
    if (!this._cacheDirty) return;
    this._cacheDirty = false;

    const allPageItems = this._getAllPageActionItems();
    const items: SerializedTodoItem[] = [];
    const byPage: Record<string, SerializedTodoItem[]> = {};
    const byId = new Map<string, SerializedTodoItem>();

    Object.entries(allPageItems).forEach(([pageId, pageItems]) => {
      const pageArr: SerializedTodoItem[] = [];
      pageItems.forEach(item => {
        if (byId.has(item.id)) return;
        const priority = item.priority;
        const parsed = ActionItemPrioritySchema.safeParse(priority);
        const normalizedPriority = parsed.success
          ? parsed.data
          : ActionItemPrioritySchema.enum.Medium;
        const planned =
          typeof item.plannedDuration === 'number' &&
          Number.isFinite(item.plannedDuration) &&
          item.plannedDuration > 0
            ? Math.round(item.plannedDuration)
            : undefined;
        const normalized: SerializedTodoItem = {
          ...item,
          pageId: pageId || item.pageId,
          priority: normalizedPriority,
          plannedDuration: planned,
          focusDaily: item.focusDaily || {},
        };
        items.push(normalized);
        pageArr.push(normalized);
        byId.set(item.id, normalized);
      });
      if (pageArr.length > 0) {
        byPage[pageId] = pageArr;
      }
    });

    this._cachedItems = items;
    this._cachedItemsByPage = byPage;
    this._cachedItemsById = byId;
  }

  private _scheduleSyncToDatabase(pageId: string): void {
    if (this._isDisposed) return;
    this._pendingSyncPages.add(pageId);
    if (this._syncScheduled) return;
    this._syncScheduled = true;
    setTimeout(() => {
      this._executePendingSyncs();
    }, 100);
  }

  private _executePendingSyncs(): void {
    if (this._isDisposed) return;
    this._syncScheduled = false;
    const pages = Array.from(this._pendingSyncPages);
    this._pendingSyncPages.clear();

    for (const pageId of pages) {
      this._syncToDatabaseImmediate(pageId);
    }
      }

  public beginBatch(): void {
    this._batchDepth++;
  }

  public endBatch(): void {
    if (this._batchDepth > 0) {
      this._batchDepth--;
    }
    if (this._batchDepth === 0 && this._refreshPending) {
      this._requestRefresh();
    }
  }

  // Focused item observable
  get focusedItemId$(): LiveData<string | null> {
    return this._focusedItemId$;
  }

  get focusedSessionStartedAtMs$(): LiveData<number | null> {
    return this._focusedSessionStartedAtMs$;
  }

  private _getSnappedScheduledAt(): string {
    const SNAP_MINUTES = 30;
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const snappedMinutes =
      Math.floor(currentMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    const hour = Math.floor(snappedMinutes / 60);
    const minute = snappedMinutes % 60;
    const scheduled = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minute,
      0,
      0
    );
    return scheduled.toISOString();
  }

  public startFocusSession(itemId: string): boolean {
    const item = this.findById(itemId);
    if (!item) return false;

    const updates: Partial<SerializedTodoItem> = {
      status: 'In Progress',
    };

    if (!item.isFocused) {
      updates.isFocused = true;
      updates.scheduledAt = this._getSnappedScheduledAt();
      updates.plannedDuration =
        item.plannedDuration || DEFAULT_PLANNED_FOCUS_MINUTES;
    }

    this.updateById(itemId, updates);
    this.setFocusedItem(itemId);
    return true;
  }

  /**
   * Set the currently focused action item (top ranked in Focus Mode)
   * Passing null stops and flushes any active session.
   */
  public setFocusedItem(itemId: string | null): void {
    if (this._focusedItemId$.value === itemId) return;

    // Flush current session before switching
    this._flushFocusDelta('switch');

    // Stop if null
    if (!itemId) {
      this._focusedItemId$.next(null);
      this._focusStartedAtMs = null;
      this._focusedSessionStartedAtMs$.next(null);
      if (this._focusTick !== null) {
        window.clearInterval(this._focusTick);
        this._focusTick = null;
      }
      return;
    }

    // Start new session for the item
    const focusItem = this.findById(itemId);
    if (focusItem && !focusItem.plannedDuration) {
      const owningPageId = focusItem.pageId || '';
      this.updatePageActionItem(owningPageId, itemId, {
        plannedDuration: DEFAULT_PLANNED_FOCUS_MINUTES,
      });
    }
    this._focusedItemId$.next(itemId);
    this._focusStartedAtMs = Date.now();
    this._focusedSessionStartedAtMs$.next(this._focusStartedAtMs);
    try {
      this.getBreakdownSteps(itemId);
    } catch {
      // safe to ignore prefetch errors
    }
    if (this._focusTick === null) {
      // Flush every 5s for a tighter persistence window
      this._focusTick = window.setInterval(
        () => this._flushFocusDelta('tick'),
        5_000
      );
    }
  }

  /**
   * Batch update focused state for multiple items
   */
  public setItemsFocused(itemIds: string[], focused: boolean): void {
    for (const id of itemIds) {
      this.updateById(id, { isFocused: focused });
    }
  }

  private _flushFocusDelta(
    _reason: 'tick' | 'switch' | 'stop' | 'dispose'
  ): void {
    const itemId = this._focusedItemId$.value;
    const startedAt = this._focusStartedAtMs;
    if (!itemId || !startedAt) return;

    const now = Date.now();
    const deltaSec = Math.floor((now - startedAt) / 1000);
    if (deltaSec <= 0) return;

    const item = this.findById(itemId);
    if (!item) {
      // If item not found, just reset baseline
      this._focusStartedAtMs = now;
      return;
    }

    const nextSeconds = Math.max(0, (item.focusTimeSeconds ?? 0) + deltaSec);
    // Update daily bucket for YYYY-MM-DD
    const dateKey = (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    })();
    const prevDaily = item.focusDaily;
    const nextDaily: Record<string, number> = { ...prevDaily };
    nextDaily[dateKey] = Math.max(
      0,
      Math.floor((nextDaily[dateKey] || 0) + deltaSec)
    );
    const owningPageId = item.pageId || '';
    this.updatePageActionItem(owningPageId, itemId, {
      focusTimeSeconds: nextSeconds,
      focusDaily: nextDaily,
    } as Partial<SerializedTodoItem>);

    // Reset baseline to now for continuous session
    this._focusStartedAtMs = now;
    this._focusedSessionStartedAtMs$.next(this._focusStartedAtMs);
  }

  /**
   * Single source of truth for fetching all action items across pages and orphaned items
   * @private
   */
  private _getAllPageActionItems(): Record<string, SerializedTodoItem[]> {
    const result: Record<string, SerializedTodoItem[]> = {};
    const workspace = this.propertiesAdapter.workspace;

    if (!workspace) {
      return result;
    }

    const activePageIds = getActivePageIds(workspace.docCollection);

    // Collect items from active pages
    for (const pageId of activePageIds) {
      const items = this.propertiesAdapter.getPageActionItems(pageId);
      if (items.length > 0) {
        result[pageId] = items;
      }
    }

    // Include orphaned action items without page association
    const orphanedItems = this.propertiesAdapter.getPageActionItems('');
    if (orphanedItems.length > 0) {
      result[''] = orphanedItems;
    }

    return result;
  }

  /**
   * Unified query API - single method to query action items with full schema support
   */
  public query(options: ActionItemQuery = {}): SerializedTodoItem[] {
    const validatedOptions = ActionItemQuerySchema.parse(options);
    this._rebuildCacheIfNeeded();
    let items = this._filterDeletedItems(
      this._cachedItems,
      validatedOptions.where?.deleted
    );

    // Apply WHERE filters
    if (validatedOptions.where) {
      items = this._applyWhereFilter(items, validatedOptions.where);
    }

    // Apply ORDER BY
    if (validatedOptions.orderBy && validatedOptions.orderBy.length > 0) {
      items = this._applySorting(items, validatedOptions.orderBy);
    }

    // Apply pagination
    if (
      validatedOptions.offset !== undefined ||
      validatedOptions.limit !== undefined
    ) {
      const start = validatedOptions.offset || 0;
      const end = validatedOptions.limit
        ? start + validatedOptions.limit
        : undefined;
      items = items.slice(start, end);
    }

    return items;
  }

  private _filterDeletedItems(
    items: SerializedTodoItem[],
    deleted: boolean | undefined
  ): SerializedTodoItem[] {
    return items.filter(item =>
      deleted === true ? isActionItemDeleted(item) : !isActionItemDeleted(item)
    );
  }

  /**
   * Apply WHERE clause filtering with strict type checking
   */
  private _applyWhereFilter(
    items: SerializedTodoItem[],
    where: NonNullable<ActionItemQuery['where']>
  ): SerializedTodoItem[] {
    return items.filter(item => {
      // ID filter
      if (where.id !== undefined) {
        const ids = Array.isArray(where.id) ? where.id : [where.id];
        if (!ids.includes(item.id)) return false;
      }

      // Title filter (partial match, case insensitive)
      if (
        where.title !== undefined &&
        !(item.title || '').toLowerCase().includes(where.title.toLowerCase())
      )
        return false;

      // PageId filter
      if (where.pageId !== undefined) {
        const pageIds = Array.isArray(where.pageId)
          ? where.pageId
          : [where.pageId];
        if (!pageIds.includes(item.pageId)) return false;
      }

      // PageTitle filter (partial match, case insensitive)
      if (
        where.pageTitle !== undefined &&
        !(item.pageTitle || '')
          .toLowerCase()
          .includes(where.pageTitle.toLowerCase())
      )
        return false;

      // Status filter
      if (where.status !== undefined) {
        const statuses = Array.isArray(where.status)
          ? where.status
          : [where.status];
        if (!item.status || !statuses.includes(item.status)) return false;
      }

      // Priority (enum) filter
      if (where.priority !== undefined) {
        const priorities = Array.isArray(where.priority)
          ? where.priority
          : [where.priority];
        const p = item.priority;
        if (!p || !priorities.includes(p)) return false;
      }

      // PriorityRank filter
      if (where.priorityRank !== undefined) {
        const rank = item.priorityRank;
        if (
          where.priorityRank.eq !== undefined &&
          rank !== where.priorityRank.eq
        )
          return false;
        if (
          where.priorityRank.min !== undefined &&
          (rank === undefined || rank < where.priorityRank.min)
        )
          return false;
        if (
          where.priorityRank.max !== undefined &&
          (rank === undefined || rank > where.priorityRank.max)
        )
          return false;
      }

      // Progress filter
      if (where.progress !== undefined) {
        const progress = item.progress || 0;
        if (where.progress.eq !== undefined && progress !== where.progress.eq)
          return false;
        if (where.progress.min !== undefined && progress < where.progress.min)
          return false;
        if (where.progress.max !== undefined && progress > where.progress.max)
          return false;
      }

      // AssignedTo filter (emails)
      if (where.assignedTo !== undefined) {
        const assignedEmails = (item.assignedTo || [])
          .map(a => a.email)
          .filter((e): e is string => Boolean(e))
          .map(e => normalizeEmail(e));
        const filterEmails = where.assignedTo.map(e => normalizeEmail(e));
        const hasMatch = filterEmails.some(email =>
          assignedEmails.includes(email)
        );
        if (!hasMatch) return false;
      }

      // DueDate filter
      if (where.dueDate !== undefined) {
        const itemDate = this._parseDate(item.dueDate);
        if (where.dueDate.eq !== undefined) {
          const targetDate = this._parseDate(where.dueDate.eq);
          if (
            !itemDate ||
            !targetDate ||
            itemDate.getTime() !== targetDate.getTime()
          )
            return false;
        }
        if (where.dueDate.before !== undefined) {
          const beforeDate = this._parseDate(where.dueDate.before);
          if (!itemDate || !beforeDate || itemDate >= beforeDate) return false;
        }
        if (where.dueDate.after !== undefined) {
          const afterDate = this._parseDate(where.dueDate.after);
          if (!itemDate || !afterDate || itemDate <= afterDate) return false;
        }
      }

      // CreatedAt filter
      if (where.createdAt !== undefined) {
        const itemDate = this._parseDate(item.createdAt);
        if (
          where.createdAt.before !== undefined &&
          (!itemDate || itemDate >= where.createdAt.before)
        )
          return false;
        if (
          where.createdAt.after !== undefined &&
          (!itemDate || itemDate <= where.createdAt.after)
        )
          return false;
      }

      // CompletedAt filter
      if (where.completedAt !== undefined) {
        const hasCompletedAt = Boolean(item.completedAt);
        if (
          where.completedAt.exists !== undefined &&
          hasCompletedAt !== where.completedAt.exists
        )
          return false;
        if (
          where.completedAt.before !== undefined ||
          where.completedAt.after !== undefined
        ) {
          const completedDate = item.completedAt
            ? new Date(item.completedAt)
            : null;
          if (
            where.completedAt.before !== undefined &&
            (!completedDate || completedDate >= where.completedAt.before)
          )
            return false;
          if (
            where.completedAt.after !== undefined &&
            (!completedDate || completedDate <= where.completedAt.after)
          )
            return false;
        }
      }

      // Completed filter (derived)
      if (where.completed !== undefined) {
        const isCompleted = isActionItemCompleted(item);
        if (isCompleted !== where.completed) return false;
      }

      if (where.deleted !== undefined) {
        const isDeleted = isActionItemDeleted(item);
        if (isDeleted !== where.deleted) return false;
      }

      // isFocused filter
      if (where.isFocused !== undefined) {
        const isFocused = Boolean(item.isFocused);
        if (isFocused !== where.isFocused) return false;
      }

      return true;
    });
  }

  private _getComparableValue(
    item: SerializedTodoItem,
    field: string
  ): string | number | null {
    if (field === 'priorityRank') {
      return item.priorityRank ?? Number.POSITIVE_INFINITY;
    }
    if (field === 'priority') {
      return priorityWeight(item.priority);
    }

    const value = item[field as keyof SerializedTodoItem];
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (value === null || value === undefined) return null;
    return null;
  }

  private _applySorting(
    items: SerializedTodoItem[],
    orderBy: NonNullable<ActionItemQuery['orderBy']>
  ): SerializedTodoItem[] {
    return items.sort((a, b) => {
      for (const sort of orderBy) {
        const aVal = this._getComparableValue(a, sort.field);
        const bVal = this._getComparableValue(b, sort.field);

        let comparison = 0;
        if (aVal === null && bVal === null) comparison = 0;
        else if (aVal === null) comparison = 1;
        else if (bVal === null) comparison = -1;
        else if (aVal < bVal) comparison = -1;
        else if (aVal > bVal) comparison = 1;

        if (comparison !== 0) {
          return sort.direction === 'desc' ? -comparison : comparison;
        }
      }
      return 0;
    });
  }

  /**
   * Parse date from various formats
   */
  private _parseDate(date: unknown): Date | null {
    if (!date) return null;
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  /**
   * Query with grouping support - unified approach
   */
  public queryWithGrouping(options: ActionItemQuery = {}): ActionItemGroup[] {
    // Validate input with Zod schema
    const validatedOptions = ActionItemQuerySchema.parse(options);
    const items = this.query(validatedOptions);

    if (!validatedOptions.groupBy) {
      // Default ungrouped behavior - return items as single group
      return [
        {
          id: 'all',
          title: 'All Items',
          createDate: Date.now(),
          updateDate: Date.now(),
          items,
          metadata: {},
        },
      ];
    }

    return this._groupItems(items, validatedOptions.groupBy);
  }

  /**
   * SQL-like aggregation query with strict typing
   */
  public aggregate(
    aggregation: AggregationType,
    options: ActionItemQuery = {}
  ): number {
    // Validate inputs with Zod schemas
    const validatedAggregation = AggregationTypeSchema.parse(aggregation);
    const validatedOptions = ActionItemQuerySchema.parse(options);

    switch (validatedAggregation) {
      case 'count':
        return this.query(validatedOptions).length;
      default:
        throw new Error(`Unsupported aggregation: ${validatedAggregation}`);
    }
  }

  /**
   * Group items by specified field
   */
  private _groupItems(
    items: SerializedTodoItem[],
    groupBy: NonNullable<ActionItemQuery['groupBy']>
  ): ActionItemGroup[] {
    switch (groupBy) {
      case 'pageId':
        return this._groupByPageId(items);
      case 'createdAt':
        return this._groupByCreatedAt(items);
      case 'dueDate':
        return this._groupByDueDate(items);
      case 'status':
        return this._groupByStatus(items);
      case 'priority':
        return this._groupByPriority(items);
      default:
        return this._groupByField(items, groupBy);
    }
  }

  private _groupByField(
    items: SerializedTodoItem[],
    field: NonNullable<ActionItemQuery['groupBy']>
  ): ActionItemGroup[] {
    const grouped = new Map<
      string,
      { value: unknown; items: SerializedTodoItem[] }
    >();

    for (const item of items) {
      const value = (item as unknown as Record<string, unknown>)[
        field as string
      ];
      const key =
        value === undefined || value === null ? '__null__' : String(value);
      const existing = grouped.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        grouped.set(key, { value, items: [item] });
      }
    }

    const now = Date.now();
    const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
      if (a === '__null__') return 1;
      if (b === '__null__') return -1;
      return a.localeCompare(b);
    });

    const humanField = String(field)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, c => c.toUpperCase());

    return sortedKeys.map(key => {
      const bucket = grouped.get(key);
      const value = bucket?.value;
      const groupItems = bucket?.items ?? [];
      return {
        id: `${String(field)}:${key}`,
        title: key === '__null__' ? `No ${humanField}` : key,
        createDate: 0,
        updateDate: now,
        items: groupItems,
        metadata: { groupBy: field, groupValue: value },
      };
    });
  }

  private _groupByPageId(items: SerializedTodoItem[]): ActionItemGroup[] {
    const grouped = new Map<string, SerializedTodoItem[]>();

    items.forEach(item => {
      const pageId = item.pageId || '';
      const arr = grouped.get(pageId);
      if (arr) arr.push(item);
      else grouped.set(pageId, [item]);
    });

    const groups: ActionItemGroup[] = [];
    const workspace = this.propertiesAdapter.workspace;

    grouped.forEach((items, pageId) => {
      if (pageId === '') {
        groups.push({
          id: 'no-meeting',
          title: 'No Meeting',
          createDate: Date.now(),
          updateDate: Date.now(),
          items,
          metadata: {
            groupBy: 'pageId',
            groupValue: '',
            pageId: '',
            isDocumentGroup: true,
          },
        });
      } else {
        const pageMeta = workspace?.docCollection.meta.getDocMeta(pageId);
        const createDate = pageMeta?.createDate || Date.now();
        const rawTitle = (pageMeta?.title || 'Untitled').trim();
        const withoutTag = rawTitle
          .replace(/\s*@upcoming-meetings\b/gi, '')
          .replace(/\s{2,}/g, ' ')
          .trim();

        // Extract ISO date (YYYY-MM-DD) if present so recurring meetings are distinct.
        const isoMatch = withoutTag.match(/\b(\d{4}-\d{2}-\d{2})\b/);
        const isoDate = isoMatch && isoMatch[1] ? isoMatch[1] : undefined;
        const meetingName = (() => {
          const matchedPart = isoMatch?.[0];
          if (!matchedPart) return withoutTag || rawTitle || 'Untitled';
          const stripped = withoutTag
            .replace(matchedPart, '')
            .replace(/\s{2,}/g, ' ')
            .trim()
            .replace(/[-–—|•·]+\s*$/g, '')
            .trim();
          return stripped || withoutTag || rawTitle || 'Untitled';
        })();

        const dateSource: Date | string = isoDate
          ? isoDate
          : new Date(createDate);
        const meetingDateLabel =
          formatRelativeDayLabel(dateSource) ||
          formatDueDateForGroupTitle(dateSource);
        groups.push({
          id: pageId,
          title: meetingName,
          createDate,
          updateDate: pageMeta?.updatedDate || Date.now(),
          items,
          metadata: {
            groupBy: 'pageId',
            groupValue: pageId,
            pageId,
            isDocumentGroup: true,
            dueDateGroup: isoDate,
            meetingDateLabel,
          },
        });
      }
    });

    return groups.sort((a, b) => b.createDate - a.createDate);
  }

  private _groupByPriority(items: SerializedTodoItem[]): ActionItemGroup[] {
    const buckets: Record<ActionItemPriority, SerializedTodoItem[]> = {
      High: [],
      Medium: [],
      Low: [],
    };

    items.forEach(item => {
      const p = item.priority;
      const parsed = ActionItemPrioritySchema.safeParse(p);
      const key: ActionItemPriority = parsed.success ? parsed.data : 'Medium';
      buckets[key].push(item);
    });

    const now = Date.now();
    const groups: ActionItemGroup[] = [];

    PRIORITY_ORDER.forEach(p => {
      const arr = buckets[p];
      if (arr.length === 0) return;
      groups.push({
        id: `priority-${p.toLowerCase()}`,
        title: `${p} Priority`,
        createDate: 0,
        updateDate: now,
        items: arr,
        metadata: { groupBy: 'priority', groupValue: p, status: 'priority' },
      });
    });

    return groups;
  }

  private _groupByCreatedAt(items: SerializedTodoItem[]): ActionItemGroup[] {
    const grouped = new Map<string, SerializedTodoItem[]>();

    for (const item of items) {
      const key = parseDueDateToKey(item.createdAt);
      const arr = grouped.get(key);
      if (arr) arr.push(item);
      else grouped.set(key, [item]);
    }

    const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
      if (a === NO_DUE_DATE_KEY) return 1;
      if (b === NO_DUE_DATE_KEY) return -1;
      // Most-recent first
      return b.localeCompare(a);
    });

    return sortedKeys.map(key => {
      const groupItems = grouped.get(key) ?? [];
      const firstItem = groupItems[0];
      const firstDate = firstItem?.createdAt;
      const relative = formatRelativeDayLabel(firstDate);
      return {
        id: key,
        title:
          key === NO_DUE_DATE_KEY
            ? 'No Date'
            : relative || formatDueDateForGroupTitle(firstDate),
        createDate: 0,
        updateDate: Date.now(),
        items: groupItems,
        metadata: {
          groupBy: 'createdAt',
          groupValue: key === NO_DUE_DATE_KEY ? null : key,
        },
      };
    });
  }

  private _groupByDueDate(items: SerializedTodoItem[]): ActionItemGroup[] {
    const grouped = new Map<string, SerializedTodoItem[]>();

    for (const item of items) {
      const key = parseDueDateToKey(item.dueDate);
      const arr = grouped.get(key);
      if (arr) arr.push(item);
      else grouped.set(key, [item]);
    }

    const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
      if (a === NO_DUE_DATE_KEY) return 1;
      if (b === NO_DUE_DATE_KEY) return -1;
      return b.localeCompare(a);
    });

    return sortedKeys.map(key => {
      const groupItems = grouped.get(key) ?? [];
      const firstItem = groupItems[0];
      const firstDueDate = firstItem?.dueDate;
      const relative = formatRelativeDayLabel(firstDueDate);
      return {
        id: key,
        title:
          key === NO_DUE_DATE_KEY
            ? NO_DUE_DATE_LABEL
            : relative || formatDueDateForGroupTitle(firstDueDate),
        createDate: 0,
        updateDate: Date.now(),
        items: groupItems,
        metadata: {
          groupBy: 'dueDate',
          groupValue: key === NO_DUE_DATE_KEY ? null : key,
          dueDate: key === NO_DUE_DATE_KEY ? undefined : key,
        },
      };
    });
  }

  private _groupByStatus(items: SerializedTodoItem[]): ActionItemGroup[] {
    const grouped = new Map<string, SerializedTodoItem[]>();

    items.forEach(item => {
      const status = item.status || 'No Status';
      const arr = grouped.get(status);
      if (arr) arr.push(item);
      else grouped.set(status, [item]);
    });

    const groups: ActionItemGroup[] = [];
    grouped.forEach((items, status) => {
      const safeStatus = status || 'No Status';
      groups.push({
        id: `status-${safeStatus.toLowerCase().replace(/\s+/g, '-')}`,
        title: safeStatus,
        createDate: 0,
        updateDate: Date.now(),
        items,
        metadata: { groupBy: 'status', groupValue: status, status },
      });
    });

    return groups;
  }

  removePageActionItems(pageId: string): void {
    this.propertiesAdapter.setPageActionItems(pageId, []);
    this._markCacheDirty();
    this._requestRefresh();
    this._scheduleSyncToDatabase(pageId);
  }

  private addPageActionItem(pageId: string, item: SerializedTodoItem): void {
    this.propertiesAdapter.addPageActionItem(pageId, item);
    this._markCacheDirty();
    this._requestRefresh();
    this._scheduleSyncToDatabase(pageId);
  }

  private updatePageActionItem(
    pageId: string,
    itemId: string,
    updates: Partial<SerializedTodoItem>
  ): void {
    const prev = this._cachedItemsById.get(itemId) ?? this.findById(itemId);
    const nextUpdates = { ...updates } as Partial<SerializedTodoItem>;
    if (updates) {
      const finalProgress =
        typeof updates.progress === 'number'
          ? updates.progress
          : (prev?.progress ?? 0);
      const finalStatus =
        typeof updates.status === 'string' ? updates.status : prev?.status;

      const willBeCompleted =
        finalProgress >= 100 ||
        (finalStatus &&
          (finalStatus.toLowerCase().includes('done') ||
            finalStatus.toLowerCase().includes('complete')));

      const wasCompleted = prev ? isActionItemCompleted(prev) : false;

      if (willBeCompleted && !wasCompleted) {
        nextUpdates.completedAt = new Date().toISOString();
      }
      if (!willBeCompleted && wasCompleted) {
        nextUpdates.completedAt = null;
      }
    }
    this.propertiesAdapter.updatePageActionItem(pageId, itemId, nextUpdates);
    this._markCacheDirty();
    this._requestRefresh();
    this._scheduleSyncToDatabase(pageId);
  }

  private trashPageActionItem(pageId: string, itemId: string): void {
    this.propertiesAdapter.updatePageActionItem(pageId, itemId, {
      deletedAt: new Date().toISOString(),
    });
    this._markCacheDirty();
    this._requestRefresh();
    this._scheduleSyncToDatabase(pageId);
  }

  private restorePageActionItem(pageId: string, itemId: string): void {
    this.propertiesAdapter.updatePageActionItem(pageId, itemId, {
      deletedAt: null,
    });
    this._markCacheDirty();
    this._requestRefresh();
    this._scheduleSyncToDatabase(pageId);
  }

  private permanentlyRemovePageActionItem(pageId: string, itemId: string): void {
    this.propertiesAdapter.removePageActionItem(pageId, itemId);
    this._markCacheDirty();
    this._requestRefresh();
    this._scheduleSyncToDatabase(pageId);
  }

  /**
   * Consolidated helpers (safe, minimal surface) for common CRUD operations
   * These helpers automatically handle orphan items (pageId: '') and lookup
   * the owning page when only an id is provided.
   */

  public findById(
    itemId: string,
    options?: { deleted?: boolean }
  ): SerializedTodoItem | undefined {
    this._rebuildCacheIfNeeded();
    const deleted = options?.deleted ?? false;
    const cached = this._cachedItemsById.get(itemId);
    if (cached && isActionItemDeleted(cached) === deleted) return cached;
    const item = this.query({ where: { id: itemId, deleted }, limit: 1 })[0];
    return item;
  }

  public updateById(
    itemId: string,
    updates: Partial<SerializedTodoItem>
  ): void {
    const item = this.findById(itemId);
    if (!item) return;
    const owningPageId = item.pageId || '';
    this.updatePageActionItem(owningPageId, itemId, updates);
  }

  public removeById(itemId: string): void {
    const item = this.findById(itemId);
    if (!item) return;
    const owningPageId = item.pageId || '';
    this.trashPageActionItem(owningPageId, itemId);
  }

  public restoreById(itemId: string): void {
    const item = this.findById(itemId, { deleted: true });
    if (!item) return;
    const owningPageId = item.pageId || '';
    this.restorePageActionItem(owningPageId, itemId);
  }

  public permanentlyRemoveById(itemId: string): void {
    const item = this.findById(itemId, { deleted: true });
    if (!item) return;
    const owningPageId = item.pageId || '';
    this.permanentlyRemovePageActionItem(owningPageId, itemId);
  }

  public emptyTrash(): void {
    const trashedItems = this.query({ where: { deleted: true } });
    this.beginBatch();
    try {
      for (const item of trashedItems) {
        const owningPageId = item.pageId || '';
        this.permanentlyRemovePageActionItem(owningPageId, item.id);
      }
    } finally {
      this.endBatch();
    }
  }

  private _normalizeBreakdownSteps(input: unknown): BreakdownStep[] {
    if (!Array.isArray(input)) {
      return [];
    }

    const normalized: BreakdownStep[] = [];
    const seen = new Set<string>();

    for (const step of input) {
      if (!step) continue;
      const candidate = step as Partial<BreakdownStep> & { id?: unknown };
      const rawId = candidate.id;
      if (rawId === undefined || rawId === null) continue;
      const id = typeof rawId === 'string' ? rawId : String(rawId);
      if (!id || seen.has(id)) continue;
      seen.add(id);

      normalized.push({
        id,
        title:
          typeof candidate.title === 'string'
            ? candidate.title
            : String(candidate.title ?? 'Untitled step'),
        description:
          typeof candidate.description === 'string'
            ? candidate.description
            : '',
        completed: Boolean(candidate.completed),
      });
    }

    return normalized;
  }

  public getBreakdownSteps(itemId: string): BreakdownStep[] {
    const item = this.findById(itemId);
    if (!item) {
      return [];
    }

    const existing = this._normalizeBreakdownSteps(item.breakdownSteps);
    if (existing.length > 0) {
      return existing;
    }

    if (item.pageId) {
      try {
        const databaseBlock = this._databaseBlocks.get(
          item.pageId
        ) as unknown as
          | {
              getBreakdownSteps?: (rowId: string) => unknown;
            }
          | undefined;
        const fallback = databaseBlock?.getBreakdownSteps?.(item.id);
        const normalizedFallback = this._normalizeBreakdownSteps(fallback);
        if (normalizedFallback.length > 0) {
          this.updateBreakdownSteps(item.id, normalizedFallback, {
            skipDatabaseSync: true,
          });
          return normalizedFallback;
        }
      } catch (error) {
        console.warn(
          '[ActionItemsService] Failed to load breakdown steps from database',
          error
        );
      }
    }

    return [];
  }

  public updateBreakdownSteps(
    itemId: string,
    steps: BreakdownStep[] | readonly BreakdownStep[],
    options?: { skipDatabaseSync?: boolean }
  ): void {
    const item = this._cachedItemsById.get(itemId) ?? this.findById(itemId);
    if (!item) return;

    const owningPageId = item.pageId || '';
    const normalized = this._normalizeBreakdownSteps(steps);

    this.propertiesAdapter.updatePageActionItem(owningPageId, itemId, {
      breakdownSteps: normalized,
    });
    this._markCacheDirty();
    this._requestRefresh();

    if (!options?.skipDatabaseSync && owningPageId) {
      this._scheduleSyncToDatabase(owningPageId);
    }
  }

  // Narrow, explicit input for creating an item with strict typing
  public createItem(input: {
    id?: string;
    title: string;
    pageId?: string;
    pageTitle?: string;
    databaseId?: string;
    columnId?: string;
    status?: string;
    priority?: ActionItemPriority;
    priorityRank?: number;
    progress?: number;
    assignedTo?: SerializedAttendee[];
    remindLater?: string[];
    dueDate?: string | Date | null;
    closeLoop?: string;
    context?: string;
    points?: number;
    createdAt?: Date | null;
    breakdownSteps?: BreakdownStep[];
    plannedDuration?: number;
  }): SerializedTodoItem {
    const pageId = input.pageId ?? '';
    const pageTitle =
      input.pageTitle ??
      (() => {
        if (!pageId) return '';
        const meta =
          this.propertiesAdapter.workspace?.docCollection.meta.getDocMeta(
            pageId
          );
        return meta?.title || 'Untitled';
      })();

    const priority = input.priority ?? ActionItemPrioritySchema.enum.Medium;

    // Default due date to tomorrow if not provided
    const defaultDueDate = (() => {
      if (input.dueDate !== undefined) return input.dueDate;
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return new Date(
        tomorrow.getFullYear(),
        tomorrow.getMonth(),
        tomorrow.getDate()
      ).toISOString();
    })();

    const newItem: SerializedTodoItem = {
      id: input.id ?? crypto.randomUUID(),
      title: input.title,
      pageId,
      pageTitle,
      databaseId: input.databaseId ?? '',
      columnId: input.columnId ?? '',
      createdAt: input.createdAt ?? new Date(),
      status: input.status
        ? ActionItemStatusSchema.parse(input.status)
        : undefined,
      priority: priority,
      priorityRank: input.priorityRank,
      progress: input.progress,
      assignedTo: input.assignedTo,
      remindLater: input.remindLater,
      dueDate: defaultDueDate,
      closeLoop: input.closeLoop,
      context: input.context,
      completedAt: null,
      breakdownSteps: input.breakdownSteps,
      plannedDuration: input.plannedDuration ?? DEFAULT_PLANNED_FOCUS_MINUTES,
    };

    this.addPageActionItem(pageId, newItem);
    return newItem;
  }

  get actionItems$(): LiveData<Record<string, SerializedTodoItem[]>> {
    return this._actionItems$;
  }

  get actionItemsGrouped$(): LiveData<ActionItemGroup[]> {
    return this._actionItemsGrouped$;
  }

  /**
   * Refresh the observables with the latest data
   * @param showCompleted Whether to include completed items (default: false) - deprecated, use completionFilter instead
   */
  private _refreshObservables(_showCompleted: boolean): void {
    if (!this.propertiesAdapter.workspace) {
      this._actionItems$.next({});
      this._actionItemsGrouped$.next([]);
      return;
    }

    // Use unified query API
    const completedFilter =
      this._currentCompletionFilter === 'open'
        ? false
        : this._currentCompletionFilter === 'closed'
          ? true
          : undefined;

    const items = this.query({
      where:
        completedFilter !== undefined
          ? { completed: completedFilter }
          : undefined,
    });

    // Convert to old format for compatibility with existing observables
    const actionItems: Record<string, SerializedTodoItem[]> = {};
    items.forEach(item => {
      if (!actionItems[item.pageId]) actionItems[item.pageId] = [];
      actionItems[item.pageId]?.push(item);
    });

    const groupedItems = this.queryWithGrouping({
      where:
        completedFilter !== undefined
          ? { completed: completedFilter }
          : undefined,
      groupBy: this._currentGroupingStrategy,
    });

    this._actionItems$.next(actionItems);
    this._actionItemsGrouped$.next(groupedItems);
  }

  setGroupingStrategy(
    strategy: ActionItemGroupBy,
    _showCompleted: boolean = false
  ): void {
    this._currentGroupingStrategy = strategy;
    this._requestRefresh();
  }

  setCompletionFilter(completionFilter: 'open' | 'closed' | 'all'): void {
    this._currentCompletionFilter = completionFilter;
    this._requestRefresh();
  }

  registerDatabaseBlock(
    pageId: string,
    databaseBlock: DatabaseBlockComponent
  ): void {
    this._databaseBlocks.set(pageId, databaseBlock);
  }

  unregisterDatabaseBlock(pageId: string): void {
    this._databaseBlocks.delete(pageId);
    this._pendingSyncPages.delete(pageId);
    this._syncInProgress.delete(pageId);
  }

  private _syncToDatabaseImmediate(pageId: string): void {
    if (this._isDisposed) return;
    this._rebuildCacheIfNeeded();
    const items = this._cachedItemsByPage[pageId] ?? [];
    this._syncPropertiesToDatabase(pageId, items);
  }

  private _syncPropertiesToDatabase(
    pageId: string,
    items: SerializedTodoItem[]
  ): void {
    if (this._isDisposed) return;
    if (this._syncInProgress.has(pageId)) return;

    const databaseBlock = this._databaseBlocks.get(pageId);
    if (!databaseBlock) return;

    this._syncInProgress.add(pageId);

    try {
      const dataSource = databaseBlock.dataSource as unknown as BlockDataSource;
      if (!dataSource?.rows$?.value || !dataSource?.viewManager?.currentViewId$?.value) {
        return;
      }

      const currentRows = dataSource.rows$.value;
      if (!Array.isArray(currentRows)) return;

      const columnMap = this._getColumnMapping(dataSource);
      if (!columnMap.titleColId) return;

      const activeItems = items.filter(item => !isActionItemDeleted(item));
      const activeItemIds = new Set(activeItems.map(item => item.id));
      const deletedItemIds = new Set(
        items.filter(item => isActionItemDeleted(item)).map(item => item.id)
      );
      const databaseId = getDatabaseBlockId(databaseBlock);
      const activeDatabaseItemIds = databaseId
        ? new Set(
            activeItems
              .filter(item => item.databaseId === databaseId)
              .map(item => item.id)
          )
        : new Set<string>();
      const expectedRowIds =
        activeDatabaseItemIds.size > 0 ? activeDatabaseItemIds : activeItemIds;
      const shouldPruneMissingRows =
        activeItems.length === 0 || activeDatabaseItemIds.size > 0;
      const staleRowIds = currentRows.filter(
        rowId =>
          deletedItemIds.has(rowId) ||
          (shouldPruneMissingRows && !expectedRowIds.has(rowId))
      );
      if (staleRowIds.length > 0) {
        dataSource.rowDelete(staleRowIds);
      }
      const remainingRows =
        staleRowIds.length > 0
          ? currentRows.filter(rowId => !staleRowIds.includes(rowId))
          : currentRows;

      activeItems.forEach(item => {
        const resolvedRowId = this._syncItemToDatabase(
          item,
          remainingRows,
          dataSource,
          columnMap
        );

        // Also propagate breakdownSteps to database model for the resolved rowId if available
        try {
          const dbComp = this._databaseBlocks.get(pageId) as unknown as {
            model?: {
              setBreakdownSteps?: (
                rowId: string,
                steps: BreakdownStep[]
              ) => void;
            };
          };
          const steps = item.breakdownSteps;
          if (
            dbComp?.model?.setBreakdownSteps &&
            Array.isArray(steps) &&
            resolvedRowId
          ) {
            dbComp.model.setBreakdownSteps(
              resolvedRowId,
              steps.map(s => ({
                id: s.id,
                title: s.title,
                description: s.description,
                completed: Boolean(s.completed),
              }))
            );
          }
        } catch (err) {
          console.warn('[ActionItemsService] Failed to sync breakdown steps for row:', resolvedRowId, err);
        }
      });
    } catch (error) {
      console.error('[ActionItemsService] Error syncing to database:', error);
    } finally {
      this._syncInProgress.delete(pageId);
    }
  }

  private _getColumnMapping(
    dataSource: BlockDataSource
  ): DatabaseColumnMapping {
    const currentViewId = dataSource.viewManager.currentViewId$.value;
    if (!currentViewId) {
      return {
        titleColId: undefined,
        statusColId: undefined,
        assigneeColId: undefined,
        progressColId: undefined,
        dueDateColId: undefined,
        priorityColId: undefined,
      };
    }

    const view = dataSource.viewManager.viewGet(currentViewId);
    const columns = view.columns$.value;

    let titleColId: string | undefined;
    let statusColId: string | undefined;
    let progressColId: string | undefined;
    let dueDateColId: string | undefined;
    let priorityColId: string | undefined;
    let assigneeColId: string | undefined;

    for (const colId of columns) {
      const type = view.columnGetType(colId);
      const name = view.columnGetName(colId);
      if (type === 'title' && !titleColId) {
        titleColId = colId;
      } else if (type === 'select' && name === 'Status' && !statusColId) {
        statusColId = colId;
      } else if (
        !assigneeColId &&
        (name === "Who's Responsible?" ||
          name === 'Assignee' ||
          name === 'Assigned To' ||
          name === 'Assign To')
      ) {
        assigneeColId = colId;
      } else if (type === 'progress' && !progressColId) {
        progressColId = colId;
      } else if (type === 'date' && name === 'Due Date' && !dueDateColId) {
        dueDateColId = colId;
      } else if (type === 'select' && name === 'Priority' && !priorityColId) {
        priorityColId = colId;
      }
    }

    return {
      titleColId,
      statusColId,
      assigneeColId,
      progressColId,
      dueDateColId,
      priorityColId,
    };
  }

  private _syncItemToDatabase(
    item: SerializedTodoItem,
    currentRows: string[],
    dataSource: BlockDataSource,
    columnMap: DatabaseColumnMapping
  ): string {
    const rowExists = currentRows.includes(item.id);

    if (!rowExists) {
      return item.id;
    }

    if (columnMap.titleColId && typeof item.title === 'string' && item.title) {
      dataSource.cellChangeValue(item.id, columnMap.titleColId, item.title);
    }

    if (columnMap.statusColId && typeof item.status === 'string' && item.status) {
      this._updateSelectColumn(
        dataSource,
        item.id,
        columnMap.statusColId,
        item.status
      );
    }

    if (columnMap.progressColId && typeof item.progress === 'number') {
      dataSource.cellChangeValue(
        item.id,
        columnMap.progressColId,
        item.progress
      );
    }

    if (columnMap.dueDateColId && item.dueDate) {
      let ms: number | undefined;
      if (typeof item.dueDate === 'string') {
        const parsed = Date.parse(item.dueDate);
        ms = isNaN(parsed) ? undefined : parsed;
      } else if (item.dueDate instanceof Date) {
        ms = item.dueDate.getTime();
      } else if (typeof item.dueDate === 'number') {
        ms = item.dueDate as number;
      }
      if (typeof ms === 'number') {
        dataSource.cellChangeValue(item.id, columnMap.dueDateColId, ms);
      }
    }

    if (
      columnMap.assigneeColId &&
      Array.isArray(item.assignedTo) &&
      item.assignedTo.length > 0
    ) {
      const viewId = dataSource.viewManager.currentViewId$.value;
      if (viewId) {
        const view = dataSource.viewManager.viewGet(viewId);
        const data = view.columnGetData(columnMap.assigneeColId);
        if (hasSelectOptions(data)) {
          const optionIds: string[] = [];
          const options = data.options;
          for (const att of item.assignedTo) {
            const attEmail = att.email;
            if (!attEmail) continue;
            const email = normalizeEmail(attEmail);
            const found =
              options.find(opt => normalizeEmail(opt.value) === email) ||
              options.find(opt => normalizeEmail(opt.id) === email);
            if (found) optionIds.push(found.id);
          }
          if (optionIds.length > 0) {
            dataSource.cellChangeValue(
              item.id,
              columnMap.assigneeColId,
              optionIds
            );
          }
        }
      }
    }

    if (columnMap.priorityColId && typeof item.priority === 'string' && item.priority) {
      this._updateSelectColumn(
        dataSource,
        item.id,
        columnMap.priorityColId,
        item.priority
      );
    }

    return item.id;
  }

  private _updateSelectColumn(
    dataSource: BlockDataSource,
    rowId: string,
    columnId: string,
    value: string
  ): void {
    if (!value) return;
    const viewId = dataSource.viewManager.currentViewId$.value;
    if (!viewId) return;
    const view = dataSource.viewManager.viewGet(viewId);
    const type = view.columnGetType(columnId);
    if (type !== 'select' && type !== 'multi-select') return;
    const data = view.columnGetData(columnId);
    if (!hasSelectOptions(data)) return;
    const options = data.options;
    const valueLower = value.toLowerCase();
    const option = options.find(
      opt => opt.value === value || opt.value.toLowerCase() === valueLower
    );
    if (option) {
      dataSource.cellChangeValue(rowId, columnId, option.id);
    }
  }

  public dispose(): void {
    this._isDisposed = true;

    try {
      this._flushFocusDelta('dispose');
    } catch {}

    if (this._focusTick !== null) {
      window.clearInterval(this._focusTick);
      this._focusTick = null;
    }
    this._focusedItemId$.next(null);
    this._focusedSessionStartedAtMs$.next(null);

    this._databaseBlocks.clear();
    this._syncInProgress.clear();
    this._pendingSyncPages.clear();
    this._refreshScheduled = false;
    this._refreshPending = false;
    this._syncScheduled = false;

    this._cachedItems = [];
    this._cachedItemsByPage = {};
    this._cachedItemsById.clear();

    if (this._docUpdateSubscription) {
      try {
        this._docUpdateSubscription.dispose();
      } catch {}
      this._docUpdateSubscription = null;
    }
    if (this._docMetaUpdateSubscription) {
      try {
        this._docMetaUpdateSubscription.dispose();
      } catch {}
      this._docMetaUpdateSubscription = null;
    }

    try {
      super.dispose?.();
    } catch {}
  }
}
